import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../../utils/useDocumentTitle";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useNotification } from "../../context/notification";
import NudgeService from "../../services/NudgeService";

export function NudgeLogAnalytics(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    campaignId: "",
  });

  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.campaignId) params.campaignId = filters.campaignId;

      const response = await NudgeService.getNudgeLogAnalytics(params);
      setAnalytics(response);
    } catch (error) {
      addNotification(
        t("error.couldNotGetSession") || "Failed to load analytics",
        "error"
      );
    }
    setIsLoading(false);
  }, [filters, addNotification, t]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    fetchAnalytics();
  };

  return (
    <DashboardLayout isDisableCampaignSelection={true}>
      <PageContent>
        <Section>
          <SectionTitle>
            {t("nudgeLog.analyticsTitle") || "Nudge Logs Analytics"}
          </SectionTitle>

          <div style={{ marginBottom: "16px", display: "flex", gap: "16px" }}>
            <div>
              <label>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={e => handleFilterChange("startDate", e.target.value)}
              />
            </div>
            <div>
              <label>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={e => handleFilterChange("endDate", e.target.value)}
              />
            </div>
            <div>
              <label>Campaign ID</label>
              <input
                type="number"
                value={filters.campaignId}
                onChange={e => handleFilterChange("campaignId", e.target.value)}
              />
            </div>
            <button onClick={handleApplyFilters}>Apply Filters</button>
          </div>

          {isLoading ? (
            <div>Loading...</div>
          ) : !analytics ? (
            <div>No data available.</div>
          ) : (
            <div>
              <div style={{ display: "flex", gap: "24px", marginBottom: "24px" }}>
                <div>
                  <strong>Total Nudges:</strong> {analytics.total}
                </div>
                <div>
                  <strong>Total Cost:</strong> ${analytics.totalCost}
                </div>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <strong>By Type:</strong>
                <ul>
                  <li>Compliment: {analytics.byType?.compliment || 0}</li>
                  <li>Suggestion: {analytics.byType?.suggestion || 0}</li>
                </ul>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <strong>By Subject:</strong>
                <ul>
                  {analytics.bySubject?.map(item => (
                    <li key={item.subject_id}>
                      Subject {item.subject_id}: {item.count}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <strong>By Model:</strong>
                <ul>
                  {Object.entries(analytics.byModel || {}).map(([model, data]) => (
                    <li key={model}>
                      {model}: {data.count} nudges, ${data.cost}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </Section>
      </PageContent>
    </DashboardLayout>
  );
}